import { namespace } from 'wsm-tagging-utils';
import userShouldBeTracked from '../../../userShouldBeTracked';

const runMain = (win) => {
	namespace(win, 'DDC.tracking.framework.config');

	const {
		DDC: { tracking },
		com,
		newrelic
	} = win;

	/**
	 * The Main class represents the entry point of the DDC Data Tracking Framework runtime.
	 * It coordinates the construction process of an InteractionListener using the specified
	 * RuntimeBuilder to build the parts that are assembled into the tracking runtime.
	 *
	 * Main fulfills the role of Director in the Builder Design Pattern.
	 */
	tracking.framework.config.Main = class {
		/**
		 * Creates the Main object
		 *
		 * @param {tracking.framework.config.RuntimeBuilder} builder The builder used to construct
		 *    the various DDC Data Tracking Framework components.
		 * @constructor
		 *
		 * @see https://sourcemaking.com/design_patterns/builder
		 * @see tracking.framework.config.RuntimeBuilder
		 */
		constructor(builder) {
			this.log = new com.dealer.log.Logger('ws-tagging:Main');
			this.builder = builder;
			this.page = undefined;
			this.form = undefined;
			this.eventHandler = undefined;
			this.decoratorChain = undefined;
			this.listener = undefined;
			this.trackingWidget = builder?.configuration?.widgetName;
			this.trackingWidgetId = builder?.configuration?.widgetId;
			this.flags = builder?.configuration?.flags || {};
		}

		/**
		 * Utilizes the RuntimeBuilder with which this instance was created to
		 * assemble the runtime components.
		 */
		assembleRuntimeComponents() {
			this.log.debug('assembling runtime components...');
			this.builder.buildDataLayer();
			this.page = this.builder.buildPage();
			this.form = this.builder.buildForm();
			this.eventHandler = this.builder.buildEventHandler(
				this.page,
				this.form
			);
			this.decoratorChain = this.builder.buildDecoratorChain(
				this.page,
				this.eventHandler,
				this.form
			);
			this.listener = this.builder.buildInteractionListener(
				this.page,
				this.decoratorChain,
				this.form
			);
			this.log.debug('runtime components assembled.');
		}

		/**
		 * Executes configured start up commands after the DDC Tracking Framework runtime has been initialized.
		 */
		executeStartupCommands() {
			const startupCommands = this.builder.buildStartupCommands(
				this.page,
				this.eventHandler,
				this.form
			);
			if (startupCommands !== undefined && startupCommands.length > 0) {
				this.log.debug('executing startup commands...');
				for (let index = 0; index < startupCommands.length; index++) {
					const command = startupCommands[index];
					command.execute();
				}
				this.log.debug('startup commands executed.');
			}
		}

		/**
		 * Launches the DDC Tracking Framework runtime using the RuntimeBuilder with which this
		 * instance was created to assemble the runtime components then executes any startup commands.
		 */
		execute() {
			if (newrelic) {
				if (userShouldBeTracked()) {
					newrelic.addPageAction(
						`${this.trackingWidget}_widget_load`,
						{
							widgetId: this.trackingWidgetId
						}
					);
				}
			}
			this.assembleRuntimeComponents();
			this.executeStartupCommands();
		}
	};
};

export default runMain;
